import { type ReactElement } from 'react';
import { ExternalLink, Music, FileText } from 'lucide-react';
import { YouTubeEmbed } from '@/components/youtube-embed';
import { AuthedAudio, AuthedDownloadLink } from '@/components/molecules/authed-media';
import { isAudioMimeType } from '@/lib/resource-media';
import type { ArrangementResource } from '@/lib/types';

const EMPTY_STATE_COPY = 'No resources for this arrangement yet.';

function ResourceBody({ resource }: { resource: ArrangementResource }): ReactElement | null {
  if (resource.sourceType === 'youtube' && resource.url) {
    return <YouTubeEmbed url={resource.url} />;
  }
  if (resource.sourceType === 'spotify' && resource.url) {
    return (
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm text-[var(--brand)] hover:underline"
      >
        <ExternalLink className="h-3 w-3" />
        Open in Spotify
      </a>
    );
  }
  if (isAudioMimeType(resource.mimeType) && resource.downloadUrl) {
    return <AuthedAudio downloadUrl={resource.downloadUrl} />;
  }
  if (resource.mimeType === 'application/pdf' && resource.downloadUrl) {
    return <AuthedDownloadLink downloadUrl={resource.downloadUrl} label={resource.title} />;
  }
  if (resource.sourceType === 'url' && resource.url) {
    return (
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-sm text-[var(--brand)] hover:underline"
      >
        <ExternalLink className="h-3 w-3" />
        {resource.url}
      </a>
    );
  }
  return null;
}

function ResourceIcon({ resource }: { resource: ArrangementResource }): ReactElement | null {
  if (isAudioMimeType(resource.mimeType)) return <Music className="h-4 w-4 text-[var(--ink-3)]" />;
  if (resource.mimeType === 'application/pdf') return <FileText className="h-4 w-4 text-[var(--ink-3)]" />;
  if (resource.sourceType === 'youtube') return <ExternalLink className="h-4 w-4 text-[var(--ink-3)]" />;
  if (resource.sourceType === 'spotify') return <Music className="h-4 w-4 text-[var(--ink-3)]" />;
  return null;
}

export type ResourceListProps = {
  resources: ArrangementResource[];
};

export function ResourceList({ resources }: ResourceListProps): ReactElement {
  if (resources.length === 0) {
    return <p className="text-sm text-[var(--ink-3)] leading-relaxed">{EMPTY_STATE_COPY}</p>;
  }

  return (
    <div className="space-y-2">
      {resources.map((resource) => (
        <div
          key={resource.arrangementResourceId}
          className="rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] p-3 space-y-2"
        >
          <div className="flex items-center gap-2">
            <ResourceIcon resource={resource} />
            <span className="text-sm font-medium text-[var(--ink)]">{resource.title}</span>
          </div>
          <ResourceBody resource={resource} />
        </div>
      ))}
    </div>
  );
}
